// Auth context + provider for @flight/web (customer app).
// Adapted from packages/admin/src/lib/auth.tsx.
// Key difference: NO role gate — any valid user (customer or admin) can sign in.
// The BE still enforces scope; we just don't reject customer-role logins here.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { User } from '@flight/shared/types';
import { api, clearStoredTokens, setStoredTokens, ACCESS_KEY } from './api';

export type AuthStatus = 'unknown' | 'authed' | 'anon';

interface AuthState {
  status: AuthStatus;
  user: User | null;
}

interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthContextValue | null>(null);

interface LoginResponseEnvelope {
  data?: {
    accessToken?: string;
    refreshToken?: string;
    user?: User;
  };
}

interface MeResponseEnvelope {
  data?: {
    user?: User;
  };
}

async function fetchMe(): Promise<User | null> {
  try {
    const res = await api.get<MeResponseEnvelope>('/users/me');
    return res.data.data?.user ?? null;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'unknown', user: null });

  // Bootstrap: if a token is in sessionStorage, re-establish state by fetching /users/me.
  useEffect(() => {
    let cancelled = false;
    const access = sessionStorage.getItem(ACCESS_KEY);
    if (!access) {
      setState({ status: 'anon', user: null });
      return;
    }
    void (async () => {
      const me = await fetchMe();
      if (cancelled) return;
      if (me) {
        setState({ status: 'authed', user: me });
      } else {
        clearStoredTokens();
        setState({ status: 'anon', user: null });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.post<LoginResponseEnvelope>('/auth/login', {
      email,
      password,
      deviceId: 'web',
      platform: 'ios', // returns tokens in body (not httpOnly cookie)
    });
    const { accessToken, refreshToken, user } = res.data.data ?? {};
    if (!accessToken || !user) {
      throw new Error('Login response missing token or user');
    }
    setStoredTokens(accessToken, refreshToken);
    setState({ status: 'authed', user });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout', {});
    } catch {
      // ignore — still clear local state
    }
    clearStoredTokens();
    setState({ status: 'anon', user: null });
  }, []);

  return (
    <AuthCtx.Provider value={{ ...state, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be called within <AuthProvider>');
  return ctx;
}
